import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Interaction } from '@redpanda/types';
import { ConfirmModal } from '@redpanda/ui';
import { deleteInteraction } from '../services/api';
import { useInteractionStore } from '../stores/interactionStore';
import { notifyDataChanged } from '../utils/dataEvents';
import InteractionForm from './InteractionForm';
import InteractionList from './InteractionList';

interface CustomerInteractionsProps {
  customer: { id: string; name: string; archived?: boolean };
  currentUserId?: string;
  currentUserRole?: string;
}

/**
 * 客戶詳情頁的互動記錄區塊（新增／編輯表單＋列表＋刪除確認）
 * 原本只在「編輯客戶」頁看得到，客戶詳情頁看不到互動歷史
 */
const CustomerInteractions: React.FC<CustomerInteractionsProps> = ({ customer, currentUserId, currentUserRole }) => {
  const { t } = useTranslation();
  const { deleteInteraction: deleteInteractionFromStore } = useInteractionStore();
  const [refreshKey, setRefreshKey] = useState(0);
  const [editingInteraction, setEditingInteraction] = useState<Interaction | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSaved = () => {
    setRefreshKey(key => key + 1);
    setEditingInteraction(null);
    notifyDataChanged('interactions');
  };

  const confirmDelete = async () => {
    if (!deletingId) return;
    setIsDeleting(true);
    try {
      await deleteInteraction(customer.id, deletingId);
      deleteInteractionFromStore(customer.id, deletingId);
      toast.success(t('components.interactions.deleteSuccess'));
      setRefreshKey(key => key + 1);
      notifyDataChanged('interactions');
    } catch {
      toast.error(t('components.interactions.deleteError'));
    } finally {
      setIsDeleting(false);
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <InteractionForm
        customerId={customer.id}
        customer={customer}
        initialData={editingInteraction}
        onInteractionSaved={handleSaved}
        onCancelEdit={() => setEditingInteraction(null)}
      />
      <InteractionList
        customerId={customer.id}
        refreshKey={refreshKey}
        onEdit={setEditingInteraction}
        onDelete={setDeletingId}
        currentUserId={currentUserId}
        currentUserRole={currentUserRole}
      />
      <ConfirmModal
        isOpen={!!deletingId}
        onClose={() => setDeletingId(null)}
        onConfirm={confirmDelete}
        title={t('components.interactions.confirmDeleteTitle')}
        message={t('components.interactions.confirmDeleteMessage')}
        confirmText={t('common.actions.delete')}
        cancelText={t('common.actions.cancel')}
        type="danger"
        isLoading={isDeleting}
        loadingText={t('common.status.deleting')}
        t={t}
      />
    </div>
  );
};

export default CustomerInteractions;
